import { useXAxisScale, useYAxisScale, ZIndexLayer } from 'recharts';
import { DataItem, SecondaryAxisConfig } from './snapshot-chart-constants';

interface SecondaryAxisOverlayProps {
  data: DataItem[];
  secondaryAxis: SecondaryAxisConfig;
  axisId: string;
}

// Render above Recharts' bar layer (zIndex 300) so the dash + label sit on top
// of both stacked bars. See `recharts/zIndex/DefaultZIndexes.js` for the layer scale.
const OVERLAY_Z_INDEX = 1500;

export const SecondaryAxisOverlay = ({
  data,
  secondaryAxis,
  axisId
}: SecondaryAxisOverlayProps) => {
  const xScale = useXAxisScale(axisId);
  const yScale = useYAxisScale();

  if (!xScale || !yScale) return null;

  return (
    <ZIndexLayer zIndex={OVERLAY_Z_INDEX}>
      <g className='snapshot-secondary-axis-overlay'>
        {data.map(item => {
          const raw = secondaryAxis.values[item.name];
          // Skip null, NaN, and non-positive values — a zero or negative secondary
          // metric (e.g. ROAS) has no meaningful x-position on a linear scale that
          // starts at 0, and renders at or behind the axis origin.
          if (raw == null || !Number.isFinite(raw) || raw <= 0) return null;
          const dashX = xScale(raw);
          const top = yScale(item.name, { position: 'start' });
          const bottom = yScale(item.name, { position: 'end' });
          if (typeof dashX !== 'number' || typeof top !== 'number' || typeof bottom !== 'number') {
            return null;
          }
          const bandHeight = bottom - top;
          // Span from the midline of the spend bar (top half) down to the midline
          // of the contribution bar (bottom half) — i.e. ~25% to ~75% of the band.
          const dashY1 = top + bandHeight * 0.25;
          const dashY2 = top + bandHeight * 0.75;
          const centerY = (top + bottom) / 2;
          const labelText = secondaryAxis.formatter(raw);
          const labelWidth = Math.max(28, labelText.length * 6 + 6);
          return (
            <g key={item.name}>
              <line
                x1={dashX}
                x2={dashX}
                y1={dashY1}
                y2={dashY2}
                stroke={secondaryAxis.color}
                strokeWidth={2}
                strokeLinecap='round'
              />
              <rect
                x={dashX + 3}
                y={centerY - 8}
                width={labelWidth}
                height={16}
                rx={3}
                fill='white'
                stroke={secondaryAxis.color}
                strokeWidth={1}
              />
              <text
                x={dashX + 3 + labelWidth / 2}
                y={centerY}
                fill={secondaryAxis.color}
                fontSize={10}
                fontWeight={600}
                textAnchor='middle'
                dominantBaseline='central'
              >
                {labelText}
              </text>
            </g>
          );
        })}
      </g>
    </ZIndexLayer>
  );
};
